import { useQuery } from '@apollo/client';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import FocusLayout from 'layouts/FocusLayout';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import { formatCurrency, getDate } from 'utils/format-helper';

import InvestInFundDialogButton from './InvestInFundDialogButton';

const FUND_QUERY = gql(`
  query GetFundInviteForConfirmation($id: ID!) {
      fundInvite(id: $id) {
      id
      status
      minimumInvestmentAmount
      maximumInvestmentAmount
      fund {
        id
        name
        description
        image
        closingDate
        fundSize
      }
    }
  }
`);

export default function FundInviteConfirm() {
  const navigate = useNavigate();
  const [fundImageError, setFundImageError] = useState(false);
  const { fundInviteLinkId } = useParams<{ fundInviteLinkId: string }>() as {
    fundInviteLinkId: string;
  };

  const { data, loading, error, refetch } = useQuery(FUND_QUERY, {
    variables: { id: fundInviteLinkId },
  });

  if (loading) return <LoadingIndicator />;

  if (error) return <ErrorMessage error={error} refetch={refetch} />;

  const fundInvite = data?.fundInvite;
  const fund = fundInvite?.fund;

  if (!fundInvite || !fund) {
    return (
      <ErrorMessage
        type="alert"
        title="Invitation not found"
        message="This invitation could not be loaded. It may have expired or the link may be invalid."
        refetch={refetch}
      />
    );
  }

  return (
    <FocusLayout>
      <div className="pb-8">
        <button
          type="button"
          onClick={() => navigate('/invest')}
          className="group mb-6 flex items-center text-sm font-medium text-gray-600 transition hover:text-gray-900"
        >
          <ArrowLeftIcon className="mr-2 h-5 w-5 shrink-0" aria-hidden="true" />
          Go Back
        </button>

        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white px-5 py-6 sm:px-8 sm:py-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
            Fund invitation
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            You&apos;re invited to invest in <span className="text-indigo-600">{fund.name}</span>
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-600">
            Review the fund below, then confirm your commitment. We&apos;ll notify the fund manager
            of your interest; wiring instructions are sent once they confirm your investment.
          </p>
        </div>

        <section className="mt-10" aria-labelledby="fund-details-heading">
          <h2
            id="fund-details-heading"
            className="text-base font-semibold text-gray-900 sm:text-lg"
          >
            Fund details
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Information about the fund you are being invited to join.
          </p>
          <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-start">
              <div className="flex shrink-0 justify-center sm:justify-start">
                {!fundImageError && fund.image ? (
                  <img
                    src={fund.image}
                    alt=""
                    onError={() => setFundImageError(true)}
                    className="h-20 w-20 rounded-xl border border-gray-100 bg-white object-contain p-1"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-gray-100 bg-gray-100 text-2xl font-bold uppercase text-gray-600">
                    {fund.name?.charAt(0) ?? '?'}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-xl font-semibold text-gray-900">{fund.name}</h3>
                {fund.description ? (
                  <p
                    dangerouslySetInnerHTML={{ __html: fund.description }}
                    className="mt-2 text-sm leading-relaxed text-gray-600 whitespace-pre-wrap"
                  ></p>
                ) : null}
              </div>
            </div>
            <dl className="grid grid-cols-1 gap-px bg-gray-100 sm:grid-cols-2">
              <div className="bg-gray-50 px-6 py-4">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Fund size
                </dt>
                <dd className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                  {fund.fundSize != null ? formatCurrency(fund.fundSize) : '—'}
                </dd>
              </div>
              <div className="bg-gray-50 px-6 py-4">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Fund closing
                </dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {fund.closingDate ? getDate(fund.closingDate) : '—'}
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="mt-12" aria-labelledby="confirm-heading">
          <h2 id="confirm-heading" className="text-base font-semibold text-gray-900 sm:text-lg">
            Confirm your investment
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Enter your amount, entity, and funding source to submit your interest.
          </p>
          <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
            <InvestInFundDialogButton
              fundId={fund.id}
              fundName={fund.name}
              minimumInvestment={fundInvite.minimumInvestmentAmount || undefined}
              maximumInvestment={fundInvite.maximumInvestmentAmount || undefined}
            />
          </div>
        </section>
      </div>
    </FocusLayout>
  );
}
